import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from 'react-query';
import { datadogLogs } from '@datadog/browser-logs';
import { Res } from '../../../resources';

export const baseApi = Res.api.gentian.BFF_BASE_URL;

export const buildAuthHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

// Logs the API error via Datadog and always throws — use inside `if (!response.ok)` guards.
export const throwApiError = async (
  response: Response,
  name: string,
  context?: Record<string, unknown>,
): Promise<never> => {
  let text = '';
  let error: unknown = null;
  try {
    error = await response.json();
    text = (error as { message?: string })?.message ?? '';
  } finally {
    datadogLogs.logger.error(
      `${response.status}: ${response.statusText} - ${text}`,
      {
        name,
        baseURL: baseApi,
        response: { status: response.status, statusText: response.statusText },
        error,
        ...context,
      },
      error as Error,
    );
    throw new Error(`${response.status}: ${response.statusText} - ${text}`);
  }
};

export const useRetrieveToken = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const retrieveToken = async (source: string) => {
    datadogLogs.logger.info(`requesting token for ${source}`, {
      name: source,
      isAuthenticated,
    });
    if (!isAuthenticated) {
      datadogLogs.logger.info('User is not authenticated. Ignoring query', { source, isAuthenticated });
    }

    const token = await getAccessTokenSilently();
    if (!!token) return token;

    datadogLogs.logger.info('No token found in the local storage. Canceling queries and mutations.', {
      name: source,
    });

    await queryClient.cancelQueries();
    await queryClient.cancelMutations();

    return null;
  };

  return { retrieveToken, isAuthenticated, getAccessTokenSilently };
};
